import {
  Alert,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import moment, { Moment } from 'moment';
import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auctionApi, AuctionStatus } from 'src/api/auctionApi';
import { MuiModal } from 'src/components/material/MuiModal';
import { MuiSelect } from 'src/components/material/MuiSelect';
import { useAppDispatch } from 'src/hooks/Redux/useAppDispatch';
import {
  fetchAuctionIfLeader,
  fetchCircleBase,
} from 'src/redux/slices/circleDetailsSlice/circleDetailsSlice';
import styles from './CreateAuctionModal.module.scss';
import { dateTimeStyles, DURATION_OPTIONS } from './const';

interface CreateAuctionModalProps {
  isOpenModal: boolean;
  setIsOpenModal: (open: boolean) => void;
  circleId: string;
  circleName: string;
  leaderName: string;
  tribeAvatar: string;
}

export const CreateAuctionModal: FC<CreateAuctionModalProps> = ({
  isOpenModal,
  setIsOpenModal,
  circleId,
  circleName,
  leaderName,
  tribeAvatar,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [duration, setDuration] = useState<string>('148');
  const [auctionStartMode, setAuctionStartMode] = useState<
    'immediately' | 'with-delay'
  >('immediately');
  const [startDate, setStartDate] = useState<Moment>(moment());
  const [auctionSize, setAuctionSize] = useState<string>('20');
  const [creationStatus, setCreationStatus] = useState<AuctionStatus>(
    AuctionStatus.CREATING
  );
  const [errorMessage, setErrorMessage] = useState<string>('');

  // const currentMemberCount = 30;

  const maxAuctionSize = useMemo(
    () => Math.min(147, 147),
    []
    // [currentMemberCount]
  );

  // const minAuctionSize = process.env.REACT_APP_API_ENV === Environment.PROD ? 10 : 2;
  const minAuctionSize = 2;

  const AUCTION_SIZE_OPTIONS = useMemo(
    () =>
      Array.from({ length: maxAuctionSize - minAuctionSize + 1 }, (_, i) => ({
        label: (i + minAuctionSize).toString(),
        value: (i + minAuctionSize).toString(),
      })),
    [maxAuctionSize, minAuctionSize]
  );

  const endDate = useMemo(
    () =>
      moment(startDate)
        .add(Number(duration), 'minutes')
        .format('MMM D, YYYY HH:mma'),
    [startDate, duration]
  );

  const startAuction = async () => {
    setErrorMessage('');
    setCreationStatus(AuctionStatus.PENDING);

    const openDate = auctionStartMode === 'immediately' ? moment() : startDate;
    const closeDate = moment(openDate).add(Number(duration), 'minutes');

    try {
      await auctionApi.createAuction(circleId, {
        open_seat_count: Number(auctionSize),
        openDate: openDate.toDate(),
        closeDate: closeDate.toDate(),
      });

      await dispatch(fetchCircleBase(circleId));
      await dispatch(fetchAuctionIfLeader(circleId));

      setCreationStatus(AuctionStatus.PENDING);

      setIsOpenModal(false);
      navigate(`/circle/${circleId}`, { replace: true });
    } catch (error: any) {
      console.error('Failed to create auction:', error);
      setCreationStatus(AuctionStatus.ERROR);
      setErrorMessage(
        error?.response?.data?.message || 'Failed to create auction'
      );
    }
  };

  const retryCreation = () => {
    setCreationStatus(AuctionStatus.CREATING);
    setErrorMessage('');
  };

  const isPending = creationStatus === AuctionStatus.PENDING;
  const isError = creationStatus === AuctionStatus.ERROR;

  return (
    <MuiModal open={isOpenModal} setOpen={setIsOpenModal} width={400}>
      <div className={styles.title}>Create an Auction</div>
      <div className={styles.wrapper}>
        <div className={styles.label}>Choose Auction Start Time</div>
        <RadioGroup
          value={auctionStartMode}
          onChange={(event) =>
            setAuctionStartMode(
              event.target.value as 'immediately' | 'with-delay'
            )
          }
        >
          <FormControlLabel
            value="with-delay"
            control={<Radio />}
            label="With Delay"
          />
          <FormControlLabel
            value="immediately"
            control={<Radio />}
            label="Immediately"
          />
        </RadioGroup>
      </div>
      {auctionStartMode === 'with-delay' && (
        <div className={styles.wrapper}>
          <div className={styles.label}>Start Date</div>
          <DateTimePicker
            value={startDate}
            onChange={(newDate) => newDate && setStartDate(moment(newDate))}
            views={['year', 'month', 'day', 'hours']}
            slotProps={dateTimeStyles}
            sx={dateTimeStyles.sx}
          />
        </div>
      )}
      <div className={styles.wrapperMiddle}>
        <div className={styles.label}>Duration (in hours)</div>
        <MuiSelect
          label="Duration"
          value={duration}
          options={DURATION_OPTIONS}
          onChange={(event: SelectChangeEvent) =>
            setDuration(event.target.value)
          }
          width="100%"
        />
      </div>
      <div className={styles.wrapper}>
        <div className={styles.label}>Auction Size</div>
        <MuiSelect
          label="Auction size"
          value={auctionSize}
          options={AUCTION_SIZE_OPTIONS}
          onChange={(event: SelectChangeEvent) =>
            setAuctionSize(event.target.value)
          }
        />
      </div>
      <div className={styles.wrapperBottom}>
        <div className={styles.label}>End of Auction</div>
        <div className={styles.endDate}>{endDate}</div>
      </div>

      {creationStatus === AuctionStatus.ERROR && (
        <Alert icon={false} severity="error">
          {errorMessage}
        </Alert>
      )}
      <button
        className={`${styles.btn} ${isError ? styles.errorBtn : ''}`}
        onClick={isError ? retryCreation : startAuction}
        disabled={isPending}
      >
        {isPending ? (
          <CircularProgress size={24} style={{ color: 'white' }} />
        ) : isError ? (
          'Try Again'
        ) : (
          'Start Auction'
        )}
      </button>
    </MuiModal>
  );
};
